/**
 * Whether the dashboard onboarding card should show a "Cargá tus CAE" account
 * step, and whether it is done. Pure so it can be unit-tested without rendering
 * the card.
 *
 * - applicable: the tenant must emit CFEs (`!bypassDgi`) AND not already get a
 *   CAE row from the DGI auto-flow group (`!autoFlowApplies`) — auto-flow
 *   tenants see `cae_download` there, so showing this too would double the row.
 *
 * - done: EITHER completion nudge, or evidence that the step is already done.
 *
 *   The two nudges exist because two flows complete this step and each stamps
 *   its own id: the self flow marks `onboarding-cae`, the accountant flow marks
 *   `onboarding-acct-cae-loaded`. Reading only the first is what let an
 *   accountant-onboarded tenant with 2 CAEs and 47 emitted invoices sit at
 *   "Cargá tus CAE" for three months (DANIEL GARAZA, reported 2026-09-09; 16
 *   tenants were in that state).
 *
 *   Completion is deliberately NOT "has >= 1 CAE": a tenant loads as many CAE
 *   types as they use (e-Factura, e-Ticket, their NC/ND…) and the row must not
 *   vanish after the first one.
 *
 *   Evidence-based completion — "this tenant is already emitting, so the step
 *   is obviously done" — is handled ONCE server-side by
 *   scripts/backfill-cae-step-done.ts, not here. The card has no emitted-CFE
 *   count: the nearest-looking signal, `cfeEmitidosPending`, is another
 *   DISMISSAL flag (`onboarding-cfe-import`), not a count, and reading it as
 *   evidence would be wrong for any tenant who emits without dismissing that
 *   separate step.
 */
export function computeCaeAccountStep(opts: {
  bypassDgi: boolean;
  autoFlowApplies: boolean;
  /** "Marcar como hecho" on the dashboard card (`onboarding-cae`). */
  dismissedSelf: boolean;
  /** The accountant flow's equivalent (`onboarding-acct-cae-loaded`). */
  dismissedAccountant: boolean;
}): { applicable: boolean; done: boolean } {
  const applicable = !opts.bypassDgi && !opts.autoFlowApplies;
  const done = opts.dismissedSelf || opts.dismissedAccountant;
  return { applicable, done };
}
